import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ApiError, api, type Me } from './api.ts';
import { IconSprite } from './components/Icon.tsx';
import { LoginScreen } from './screens/LoginScreen.tsx';
import { CollectionScreen } from './screens/CollectionScreen.tsx';
import { SpecimenScreen } from './screens/SpecimenScreen.tsx';
import { SpecimenFormScreen } from './screens/SpecimenFormScreen.tsx';
import { TripScreen } from './screens/TripScreen.tsx';
import { TripFormScreen } from './screens/TripFormScreen.tsx';
import { PublicIdScreen } from './screens/PublicIdScreen.tsx';
import { ExportScreen } from './screens/ExportScreen.tsx';
import { SettingsScreen } from './screens/SettingsScreen.tsx';
import { StatsScreen } from './screens/StatsScreen.tsx';
import { LabelsScreen } from './screens/LabelsScreen.tsx';
import { ScannerScreen } from './screens/ScannerScreen.tsx';
import { AppraiseScreen } from './screens/AppraiseScreen.tsx';
import { ReferenceScreen } from './screens/ReferenceScreen.tsx';
import { MapScreen } from './screens/MapScreen.tsx';

type Session = { state: 'loading' } | { state: 'in'; me: Me } | { state: 'out' };

export function App() {
  const [session, setSession] = useState<Session>({ state: 'loading' });

  const refresh = useCallback(async () => {
    try {
      setSession({ state: 'in', me: await api.me() });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setSession({ state: 'out' });
      } else {
        // Сеть легла: показываем вход, а не белый экран.
        setSession({ state: 'out' });
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (session.state === 'loading') {
    return (
      <>
        <IconSprite />
        <div className="app">
          <div className="scroll centered muted">Загрузка…</div>
        </div>
      </>
    );
  }

  return (
    <BrowserRouter>
      <IconSprite />
      {session.state === 'out' ? (
        /**
         * Пока не вошли, любой адрес ведёт на форму входа, но сам адрес
         * сохраняется: переход по QR-ссылке /s/<code> после входа продолжится
         * туда же, а не на главную (ТЗ 4.5).
         */
        <Routes>
          <Route path="*" element={<LoginScreen onSignedIn={refresh} />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/" element={<CollectionScreen me={session.me} />} />
          <Route path="/new" element={<SpecimenFormScreen me={session.me} />} />
          <Route path="/specimen/:id" element={<SpecimenScreen />} />
          <Route path="/specimen/:id/edit" element={<SpecimenFormScreen me={session.me} />} />
          <Route path="/trips/new" element={<TripFormScreen />} />
          <Route path="/trips/:id" element={<TripScreen />} />
          <Route path="/trips/:id/edit" element={<TripFormScreen />} />
          <Route path="/s/:publicId" element={<PublicIdScreen />} />
          <Route path="/export" element={<ExportScreen me={session.me} />} />
          <Route path="/stats" element={<StatsScreen />} />
          <Route path="/labels" element={<LabelsScreen />} />
          <Route path="/scan" element={<ScannerScreen />} />
          <Route path="/reference" element={<ReferenceScreen />} />
          <Route path="/appraise" element={<AppraiseScreen />} />
          <Route path="/map" element={<MapScreen />} />
          <Route
            path="/settings"
            element={
              <SettingsScreen me={session.me} onSignedOut={refresh} onProfileChanged={refresh} />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </BrowserRouter>
  );
}
